import React, { useState, useEffect, memo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Breadcrumb from '../components/breadcrumb';
import { ContractorService } from '../services/contractor.service';
import { toast } from 'react-toastify';
import { authService } from '../services/auth.service';
import { getContractorManagementAccess } from '../config/roleAccess';
import { MechanicalContractor } from '../components/mechanical-contractor-management/types';
import MechanicalContractorDialogs from '../components/mechanical-contractor-management/MechanicalContractorDialogs';
import MechanicalContractorDetailsCard from '../components/mechanical-contractor-management/MechanicalContractorDetailsCard';
import MechanicalContractorLocalTrades from '../components/mechanical-contractor-management/MechanicalContractorLocalTrades';
import { MechanicalContractorFormData, MechanicalContractorFormErrors, initialMechanicalContractorFormData, initialMechanicalContractorFormErrors } from '../components/mechanical-contractor-management/dialogTypes';
import { validateStep1, validateStep2 } from '../components/mechanical-contractor-management/validation';
import { lookupService } from '../services/lookups.service';
import { getCoordinatesFromAddress } from '../components/contractor-management/utils';


// Navigation Component
const Navigation = memo(({ onBack }: { onBack: () => void }) => (
  <div className="mb-6 flex items-center">
    <ArrowLeft
      className="cursor-pointer text-gray-600 hover:text-gray-800 mr-3"
      size={20}
      onClick={onBack}
      data-testid="back-arrow"
    />
    <Breadcrumb title="Mechanical Contractor Details" />
  </div>
));

Navigation.displayName = 'Navigation';

// Main Component
const MechanicalContractorDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [contractor, setContractor] = useState<MechanicalContractor | null>(null);
  const [loading, setLoading] = useState(true);
  const user = authService.getUser();
  const userPermissions = getContractorManagementAccess(user?.role || '');

  // Dialog states
  const [showAddMechanicalContractorDialog, setShowAddMechanicalContractorDialog] = useState(false);
  const [showSaveConfirmDialog, setShowSaveConfirmDialog] = useState(false);
  const [showCloseConfirmDialog, setShowCloseConfirmDialog] = useState(false);
  const [editFormData, setEditFormData] = useState<any>(null);
  const [pendingCloseAction, setPendingCloseAction] = useState<null | (() => void)>(null);

  // Form states
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<MechanicalContractorFormData>(initialMechanicalContractorFormData);
  const [errors, setErrors] = useState<MechanicalContractorFormErrors>(initialMechanicalContractorFormErrors);
  const [hasShownSimilarNameWarning, setHasShownSimilarNameWarning] = useState(false);

  // Province options
  const [provinceOptions, setProvinceOptions] = useState<any[]>([]);
  const [isLoadingProvinces, setIsLoadingProvinces] = useState(false);

  // Deactivation eligibility
  const [canDeactivate, setCanDeactivate] = useState<boolean>(true);
  const [deactivationReason, setDeactivationReason] = useState<string>('');

  // Refs
  const addMechanicalContractorDialogContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchContractor = async () => {
      if (!id) {
        toast.error('Contractor ID not provided');
        navigate('/mechanical-contractor-management');
        return;
      }

      try {
        setLoading(true);
        const response = await ContractorService.getContractorById(id);
        setContractor((response as any).data as MechanicalContractor);
      } catch (error) {
        console.error('Error fetching contractor:', error);
        toast.error('Failed to load contractor details');
        navigate('/mechanical-contractor-management');
      } finally {
        setLoading(false);
      }
    };

    fetchContractor();
  }, [id, navigate]);

  // Load provinces on mount
  useEffect(() => {
    const loadProvinces = async () => {
      setIsLoadingProvinces(true);
      try {
        const provinces = await lookupService.getProvinces();
        setProvinceOptions(provinces);
      } catch (error) {
        console.error('Error loading provinces:', error);
      } finally {
        setIsLoadingProvinces(false);
      }
    };
    loadProvinces();
  }, []);

  // Check deactivation eligibility when editing a contractor
  useEffect(() => {
    const checkEligibility = async () => {
      if (editFormData?.id && editFormData.status === 'active') {
        try {
          const eligibility = await ContractorService.checkDeactivationEligibility(editFormData.id);
          setCanDeactivate(eligibility.canDeactivate);
          setDeactivationReason(eligibility.reason || '');
        } catch (error) {
          console.error('Error checking deactivation eligibility:', error);
          setCanDeactivate(true);
          setDeactivationReason('');
        }
      } else {
        setCanDeactivate(true);
        setDeactivationReason('');
      }
    };

    if (showAddMechanicalContractorDialog) {
      checkEligibility();
    }
  }, [editFormData, showAddMechanicalContractorDialog]);

  const handleBack = () => {
    navigate('/mechanical-contractor-management');
  };

  const resetFormData = () => {
    setFormData(initialMechanicalContractorFormData);
    setCurrentStep(1);
    setErrors(initialMechanicalContractorFormErrors);
  };

  const handleEdit = () => {
    if (!contractor) return;

    // Format contractor data for the form
    const headOfficeAddress = contractor.address?.find(addr => addr.addressType === 'head-office');
    const subOffices = contractor.address?.filter(addr => addr.addressType === 'sub-office') || [];

    // Format local trade selections
    const localTradeSelections = (contractor.contractorTrades || []).reduce((acc: any[], contractorTrade) => {
      if (contractorTrade.localUnion) {
        const localKey = contractorTrade.localUnion.id;
        const existingLocal = acc.find(item => item.localUnionId === localKey);

        if (existingLocal) {
          // Add trade to existing local
          if (contractorTrade.trade && !existingLocal.tradeIds.includes(contractorTrade.trade.id)) {
            existingLocal.tradeIds.push(contractorTrade.trade.id);
          }
        } else {
          // Create new local entry
          acc.push({
            localUnionId: contractorTrade.localUnion.id,
            localUnionName: `${contractorTrade.localUnion.code} - Local ${contractorTrade.localUnion.unionNumber}`,
            tradeIds: contractorTrade.trade ? [contractorTrade.trade.id] : [],
            unionType: contractorTrade.unionType || 'union'
          });
        }
      }
      return acc;
    }, []);

    const formattedData = {
      id: contractor.id,
      name: contractor.name,
      status: contractor.status,
      address: headOfficeAddress?.address || '',
      city: headOfficeAddress?.city || '',
      province: headOfficeAddress?.province?.id || '',
      postalCode: headOfficeAddress?.postalCode || '',
      coordinates: headOfficeAddress?.coordinates || null,
      addressId: (headOfficeAddress as any)?.id || null, // Include address ID for updates
      subOffices: subOffices.map(office => ({
        id: (office as any).id, // Include address ID for updates
        address: office.address || '',
        city: office.city || '',
        province: office.province?.id || '',
        postalCode: office.postalCode || '',
        coordinates: office.coordinates || null
      })),
      localTradeSelections: localTradeSelections
    };

    setEditFormData(formattedData);
    setFormData(formattedData as any);
    setShowAddMechanicalContractorDialog(true);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear related errors when user makes changes
    if (errors[name as keyof MechanicalContractorFormErrors]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubOfficeChange = (index: number, field: string, value: string) => {
    const updatedSubOffices = [...formData.subOffices];
    updatedSubOffices[index] = {
      ...updatedSubOffices[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      subOffices: updatedSubOffices
    }));
  };

  const handleBackStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleNext = () => {
    if (currentStep === 1) {
      const isValid = validateStep1(formData, setErrors);
      if (isValid) {
        setCurrentStep(2);
      }
    } else if (currentStep === 2) {
      const isValid = validateStep2(formData, setErrors);
      if (isValid) {
        setShowSaveConfirmDialog(true);
      }
    }
  };

  const confirmSave = async (ignoreSimilarNameWarning: boolean = false) => {
    console.log('=== confirmSave called (edit mode) ===');
    console.log('ignoreSimilarNameWarning:', ignoreSimilarNameWarning);
    console.log('hasShownSimilarNameWarning:', hasShownSimilarNameWarning);

    setIsSubmitting(true);
    setShowSaveConfirmDialog(false);

    try {
      // Update coordinates for main address before saving (only if all required fields are present)
      if (formData.address && formData.city && formData.province && formData.postalCode) {
        const mainCoordinates = await getCoordinatesFromAddress(
          formData.address,
          formData.city,
          formData.province,
          formData.postalCode
        );
        if (mainCoordinates) {
          formData.coordinates = mainCoordinates;
        }
      }

      // Update coordinates for sub offices before saving
      for (let i = 0; i < formData.subOffices.length; i++) {
        const office = formData.subOffices[i];
        if (office.address && office.city && office.province && office.postalCode) {
          const coordinates = await getCoordinatesFromAddress(
            office.address,
            office.city,
            office.province,
            office.postalCode
          );
          if (coordinates) {
            formData.subOffices[i].coordinates = coordinates;
          }
        }
      }

      // Format data for API
      const contractorTrades = formData.localTradeSelections.flatMap((selection: any) => {
        // If no trades selected for this local, create one entry with just the local and union type
        if (selection.tradeIds.length === 0) {
          return [{
            localUnionId: selection.localUnionId,
            unionType: selection.unionType
          }];
        }

        // Create one entry for each trade under this local
        return selection.tradeIds.map((tradeId: string) => ({
          trade: tradeId,
          localUnionId: selection.localUnionId,
          unionType: selection.unionType
        }));
      });

      // Format addresses - only include if data is provided
      const addresses = [];

      // Include head office only if at least one field is provided
      if (formData.address || formData.city || formData.province || formData.postalCode) {
        const headOfficeData: any = {
          addressType: 'head-office',
          address: formData.address || null,
          city: formData.city || null,
          province: formData.province || null,
          postalCode: formData.postalCode || null,
          coordinates: (formData.coordinates && Array.isArray(formData.coordinates) && formData.coordinates.length > 0) ? formData.coordinates : null
        };

        // Include ID if editing existing address
        if ((formData as any).addressId) {
          headOfficeData.id = (formData as any).addressId;
        }

        addresses.push(headOfficeData);
      }

      // Add valid sub offices
      const validSubOffices = formData.subOffices.filter(office =>
        office.address || office.city || office.province || office.postalCode
      );

      addresses.push(...validSubOffices.map(office => {
        const subOfficeData: any = {
          addressType: 'sub-office',
          address: office.address || null,
          city: office.city || null,
          province: office.province || null,
          postalCode: office.postalCode || null,
          coordinates: (office.coordinates && Array.isArray(office.coordinates) && office.coordinates.length > 0) ? office.coordinates : null
        };

        // Include ID if editing existing address
        if ((office as any).id) {
          subOfficeData.id = (office as any).id;
        }

        return subOfficeData;
      }));

      const apiData: any = {
        name: formData.name,
        status: formData.status,
        contractorTrades: contractorTrades,
        address: addresses
      };

      // If we've already shown the warning, add the flag to bypass it
      if (hasShownSimilarNameWarning) {
        console.log('Adding ignoreSimilarNameWarning flag (second attempt)');
        apiData.ignoreSimilarNameWarning = true;
      }

      console.log('apiData after flag:', apiData);

      await ContractorService.updateContractor(editFormData.id, apiData);
      toast.success('Mechanical contractor updated successfully');

      // Close dialogs and refresh data
      setShowSaveConfirmDialog(false);
      setShowAddMechanicalContractorDialog(false);
      setEditFormData(null);
      resetFormData();
      setHasShownSimilarNameWarning(false); // Reset the warning flag

      // Refresh contractor data
      const response = await ContractorService.getContractorById(id!);
      setContractor((response as any).data as MechanicalContractor);
    } catch (error: any) {
      console.log('=== Error caught (edit mode) ===');
      console.log('error:', error);
      console.log('error.message:', error.message);
      console.log('error.response?.data:', error.response?.data);

      // Get the error message from either error.message or error.response.data.message
      const errorMessage = error.message || error.response?.data?.message || '';
      console.log('errorMessage:', errorMessage);

      // Check if this is a similar name warning (not a hard error)
      if (errorMessage.includes('similar name already exists') && !hasShownSimilarNameWarning) {
        console.log('Similar name warning detected - blocking update');
        const existingName = errorMessage.match(/"([^"]+)"/)?.[1] || 'another contractor';

        // Mark that we've shown the warning
        setHasShownSimilarNameWarning(true);

        // Show warning toast and do NOT proceed
        toast.warning(
          `A contractor with a similar name already exists: "${existingName}". Click "Save Changes" again to proceed.`,
          { autoClose: 5000 }
        );

        // Re-open the save confirmation dialog so user can click again
        setShowSaveConfirmDialog(true);
      } else {
        // Regular error - show error toast
        console.log('Regular error, showing toast');
        toast.error(errorMessage || 'Error saving mechanical contractor');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelSave = () => {
    setShowSaveConfirmDialog(false);
  };

  const handleCloseAddMechanicalContractorDialog = () => {
    // Check if form has changes
    const hasChanges = formData.name !== initialMechanicalContractorFormData.name ||
      formData.status !== initialMechanicalContractorFormData.status ||
      formData.address !== initialMechanicalContractorFormData.address ||
      formData.city !== initialMechanicalContractorFormData.city ||
      formData.province !== initialMechanicalContractorFormData.province ||
      formData.postalCode !== initialMechanicalContractorFormData.postalCode ||
      formData.subOffices.length !== initialMechanicalContractorFormData.subOffices.length ||
      formData.localTradeSelections.length !== initialMechanicalContractorFormData.localTradeSelections.length;

    if (!hasChanges) {
      setShowAddMechanicalContractorDialog(false);
      setEditFormData(null);
      resetFormData();
      setHasShownSimilarNameWarning(false);
      return;
    }

    // Show confirmation dialog
    setShowCloseConfirmDialog(true);
    setPendingCloseAction(() => () => {
      setShowAddMechanicalContractorDialog(false);
      setEditFormData(null);
      resetFormData();
      setShowCloseConfirmDialog(false);
      setHasShownSimilarNameWarning(false);
    });
  };

  if (loading) {
    return (
      <div className="bg-gray-50 p-3 sm:p-4 md:p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-1/2"></div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="h-20 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!contractor) {
    return (
      <div className="bg-gray-50 p-3 sm:p-4 md:p-6">
        <Navigation onBack={handleBack} />
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
          <p className="text-gray-500">Contractor not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 p-3 sm:p-4 md:p-6">
      <Navigation onBack={handleBack} />

      <MechanicalContractorDetailsCard
        contractor={contractor}
        onEdit={handleEdit}
        canEdit={userPermissions.editContractor}
      />

      <MechanicalContractorLocalTrades contractorTrades={contractor.contractorTrades} />

      <MechanicalContractorDialogs
        showAddMechanicalContractorDialog={showAddMechanicalContractorDialog}
        showSaveConfirmDialog={showSaveConfirmDialog}
        showCloseConfirmDialog={showCloseConfirmDialog}
        isSubmitting={isSubmitting}
        formData={formData}
        errors={errors}
        currentStep={currentStep}
        editFormData={editFormData}
        addMechanicalContractorDialogContentRef={addMechanicalContractorDialogContentRef}
        provinceOptions={provinceOptions}
        isLoadingProvinces={isLoadingProvinces}
        canDeactivate={canDeactivate}
        deactivationReason={deactivationReason}
        handleCloseAddMechanicalContractorDialog={handleCloseAddMechanicalContractorDialog}
        handleInputChange={handleInputChange}
        handleSubOfficeChange={handleSubOfficeChange}
        handleBack={handleBackStep}
        handleNext={handleNext}
        handleCancelSave={handleCancelSave}
        confirmSave={confirmSave}
        setShowCloseConfirmDialog={setShowCloseConfirmDialog}
        pendingCloseAction={pendingCloseAction}
        setFormData={setFormData}
      />
    </div>
  );
};

export default MechanicalContractorDetails;